import { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, Image, Calendar, MessageSquare, HelpCircle, 
  DollarSign, Settings, LogOut, Menu, X, Home, ChevronRight, Users, Mail, 
  LayoutTemplate, FolderOpen, FileText, Sliders, Database, ChevronDown
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';

// Admin sub-pages
import DashboardHome from './DashboardHome';
import GalleryManager from './GalleryManager';
import BookingsManager from './BookingsManager';
import InquiriesManager from './InquiriesManager';
import InquiryDetailsPage from './InquiryDetailsPage';
import FAQManager from './FAQManager';
import PricingManager from './PricingManager';
import ContentManager from './ContentManager';
import RenterManager from './RenterManager';
import RenterDetailsPage from './RenterDetailsPage';
import EmailManager from './EmailManager';
import HomepageManager from './HomepageManager';
import FileLibrary from './FileLibrary';
import ContractsManager from './ContractsManager';
import ContractSettings from './ContractSettings';
import BackupManager from './BackupManager';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Menu structure with collapsible sections
const menuStructure = [
  { 
    type: 'link',
    path: '/admin/dashboard', 
    icon: LayoutDashboard, 
    label: 'Dashboard', 
    countKey: null 
  },
  {
    type: 'section',
    label: 'Renters & Inquiries',
    icon: Users,
    key: 'renters',
    items: [
      { path: '/admin/inquiries', icon: MessageSquare, label: 'Inquiries', countKey: 'newInquiries' },
      { path: '/admin/renters', icon: Users, label: 'Renters', countKey: 'renters' },
      { path: '/admin/bookings', icon: Calendar, label: 'Availability', countKey: null },
    ]
  },
  {
    type: 'section',
    label: 'Contracts',
    icon: FileText,
    key: 'contracts',
    items: [
      { path: '/admin/contracts', icon: FileText, label: 'Contracts', countKey: 'signedContracts' },
      { path: '/admin/contract-settings', icon: Sliders, label: 'Settings', countKey: null },
    ]
  },
  {
    type: 'section',
    label: 'Page Content',
    icon: LayoutTemplate,
    key: 'content',
    items: [
      { path: '/admin/homepage', icon: LayoutTemplate, label: 'Homepage Images', countKey: null },
      { path: '/admin/content', icon: Settings, label: 'Property Content', countKey: null },
      { path: '/admin/gallery', icon: Image, label: 'Gallery', countKey: null },
      { path: '/admin/pricing', icon: DollarSign, label: 'Pricing', countKey: null },
      { path: '/admin/faq', icon: HelpCircle, label: 'FAQ', countKey: null },
    ]
  },
  { 
    type: 'link',
    path: '/admin/email', 
    icon: Mail, 
    label: 'Email Client', 
    countKey: null 
  },
  { 
    type: 'link',
    path: '/admin/files', 
    icon: FolderOpen, 
    label: 'File Library', 
    countKey: null 
  },
  { 
    type: 'link',
    path: '/admin/backup', 
    icon: Database, 
    label: 'Backup & Restore', 
    countKey: null 
  },
];

// Flatten menu for breadcrumb lookup
const flattenMenu = (menu) => {
  const flat = [];
  menu.forEach(item => {
    if (item.type === 'link') {
      flat.push(item);
    } else if (item.type === 'section') {
      flat.push(...item.items);
    }
  });
  return flat;
};

const allLinks = flattenMenu(menuStructure);

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState(() => {
    // Auto-expand section containing current path
    const currentPath = location.pathname;
    const expanded = {};
    menuStructure.forEach(item => {
      if (item.type === 'section') {
        const hasActivePath = item.items.some(subItem => currentPath.startsWith(subItem.path));
        expanded[item.key] = hasActivePath;
      }
    });
    return expanded;
  });
  const [counts, setCounts] = useState({
    newInquiries: 0,
    renters: 0,
    signedContracts: 0
  });

  // Auto-expand section when navigating
  useEffect(() => {
    const currentPath = location.pathname;
    menuStructure.forEach(item => {
      if (item.type === 'section') {
        const hasActivePath = item.items.some(subItem => currentPath.startsWith(subItem.path));
        if (hasActivePath) {
          setExpandedSections(prev => ({ ...prev, [item.key]: true }));
        }
      }
    });
  }, [location.pathname]);

  useEffect(() => {
    if (!loading && !user) {
      navigate('/admin/login');
    }
  }, [user, loading, navigate]);

  // Fetch counts for sidebar
  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const [inquiriesRes, rentersRes, contractsRes] = await Promise.all([
          axios.get(`${API}/inquiries`).catch(() => ({ data: [] })),
          axios.get(`${API}/renters`).catch(() => ({ data: [] })),
          axios.get(`${API}/contracts`).catch(() => ({ data: [] }))
        ]);

        const newInquiries = inquiriesRes.data.filter(i => i.status === 'new' || !i.status).length;
        
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const renters = rentersRes.data.filter(r => {
          if (!r.check_out_date) return false;
          const checkOutDate = new Date(r.check_out_date);
          return checkOutDate >= today;
        }).length;
        
        const signedContracts = contractsRes.data.filter(c => c.status === 'signed').length;

        setCounts({
          newInquiries,
          renters,
          signedContracts
        });
      } catch (error) {
        console.error('Failed to fetch sidebar counts:', error);
      }
    };

    if (user) {
      fetchCounts();
      const interval = setInterval(fetchCounts, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  // Refresh counts when navigating
  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const [inquiriesRes, rentersRes, contractsRes] = await Promise.all([
          axios.get(`${API}/inquiries`).catch(() => ({ data: [] })),
          axios.get(`${API}/renters`).catch(() => ({ data: [] })),
          axios.get(`${API}/contracts`).catch(() => ({ data: [] }))
        ]);

        const newInquiries = inquiriesRes.data.filter(i => i.status === 'new' || !i.status).length;
        
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const renters = rentersRes.data.filter(r => {
          if (!r.check_out_date) return false;
          const checkOutDate = new Date(r.check_out_date);
          return checkOutDate >= today;
        }).length;
        
        const signedContracts = contractsRes.data.filter(c => c.status === 'signed').length;

        setCounts({
          newInquiries,
          renters,
          signedContracts
        });
      } catch (error) {
        console.error('Failed to fetch sidebar counts:', error);
      }
    };

    if (user) {
      fetchCounts();
    }
  }, [location.pathname, user]);

  const toggleSection = (key) => {
    setExpandedSections(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Calculate section total count
  const getSectionCount = (items) => {
    return items.reduce((total, item) => {
      if (item.countKey && counts[item.countKey]) {
        return total + counts[item.countKey];
      }
      return total;
    }, 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-[#1B4D3E] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!user) {
    return null;
  }

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const renderMenuItem = (item, isSubItem = false) => {
    const isActive = location.pathname === item.path || 
                     (item.path !== '/admin/dashboard' && location.pathname.startsWith(item.path));
    const Icon = item.icon;
    const count = item.countKey ? counts[item.countKey] : null;
    
    return (
      <Link
        key={item.path}
        to={item.path}
        onClick={() => setSidebarOpen(false)}
        className={`
          flex items-center justify-between px-4 py-2.5 rounded-lg text-sm
          transition-colors duration-200
          ${isSubItem ? 'ml-4 pl-8' : ''}
          ${isActive 
            ? 'bg-[#D4AF37] text-[#1C1C1C]' 
            : 'text-white/80 hover:bg-white/10 hover:text-white'
          }
        `}
        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <div className="flex items-center gap-3">
          <Icon className={`w-4 h-4 ${isSubItem ? '' : 'w-5 h-5'}`} />
          {item.label}
        </div>
        {count !== null && count > 0 && (
          <span className={`
            px-2 py-0.5 text-xs font-medium rounded-full
            ${isActive 
              ? 'bg-[#1B4D3E] text-white' 
              : 'bg-[#D4AF37] text-[#1B4D3E]'
            }
          `}>
            {count}
          </span>
        )}
      </Link>
    );
  };

  const renderSection = (section) => {
    const isExpanded = expandedSections[section.key];
    const hasActiveChild = section.items.some(item => 
      location.pathname === item.path || location.pathname.startsWith(item.path)
    );
    const Icon = section.icon;
    const sectionCount = getSectionCount(section.items);

    return (
      <div key={section.key} className="space-y-1">
        <button
          onClick={() => toggleSection(section.key)}
          className={`
            w-full flex items-center text-left px-4 py-2.5 rounded-lg text-sm
            transition-colors duration-200
            ${hasActiveChild 
              ? 'bg-white/10 text-white' 
              : 'text-white/80 hover:bg-white/10 hover:text-white'
            }
          `}
        >
          <div className="flex items-center gap-3 flex-1">
            <Icon className="w-5 h-5" />
            {section.label}
            {sectionCount > 0 && (
              <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-[#D4AF37] text-[#1B4D3E]">
                {sectionCount}
              </span>
            )}
          </div>
          <ChevronDown 
            className={`w-4 h-4 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} 
          />
        </button>
        
        {/* Collapsible content */}
        <div className={`
          overflow-hidden transition-all duration-200 ease-in-out
          ${isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}
        `}>
          <div className="space-y-1 py-1">
            {section.items.map(item => renderMenuItem(item, true))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6] flex">
      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50
        w-64 bg-[#1B4D3E] border-r border-[#153E32]
        transform transition-transform duration-300
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="h-full flex flex-col">
          {/* Logo */}
          <div className="p-6 border-b border-[#153E32]">
            <Link to="/admin/dashboard" className="flex items-center gap-2">
              <span className="font-serif text-xl text-white">Big Bear</span>
              <span className="text-xs text-[#D4AF37] tracking-wider">Admin</span>
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 overflow-y-auto">
            <ul className="space-y-1">
              {menuStructure.map((item) => {
                if (item.type === 'link') {
                  return <li key={item.path}>{renderMenuItem(item)}</li>;
                } else if (item.type === 'section') {
                  return <li key={item.key}>{renderSection(item)}</li>;
                }
                return null;
              })}
            </ul>
          </nav>

          {/* User & Logout */}
          <div className="p-4 border-t border-[#153E32]">
            <div className="mb-4 px-4">
              <p className="text-sm font-medium text-white">{user?.name}</p>
              <p className="text-xs text-white/60">{user?.email}</p>
            </div>
            <div className="space-y-1">
              <Link
                to="/"
                className="flex items-center gap-3 px-4 py-2 rounded-lg text-sm text-white/80 hover:bg-white/10 hover:text-white transition-colors"
              >
                <Home className="w-5 h-5" />
                View Website
              </Link>
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-2 rounded-lg text-sm text-red-300 hover:bg-red-900/30 hover:text-red-200 transition-colors"
                data-testid="logout-btn"
              >
                <LogOut className="w-5 h-5" />
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 min-w-0 bg-[#F5F3F0] relative">
        {/* Subtle background pattern */}
        <div 
          className="absolute inset-0 opacity-[0.03] pointer-events-none"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%231B4D3E' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />
        {/* Top Bar */}
        <header className="sticky top-0 z-40 bg-white border-b border-[#E2E0DC] px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="lg:hidden p-2 text-[#66605B] hover:text-[#1C1C1C]"
              >
                {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
              
              {/* Breadcrumb */}
              <nav className="flex items-center gap-2 text-sm">
                <span className="text-[#66605B]">Admin</span>
                <ChevronRight className="w-4 h-4 text-[#66605B]" />
                <span className="text-[#1C1C1C] font-medium">
                  {allLinks.find(l => location.pathname.startsWith(l.path))?.label || 'Dashboard'}
                </span>
              </nav>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <div className="p-6 relative z-10">
          <Routes>
            <Route path="dashboard" element={<DashboardHome />} />
            <Route path="renters" element={<RenterManager />} />
            <Route path="renters/:id" element={<RenterDetailsPage />} />
            <Route path="contracts" element={<ContractsManager />} />
            <Route path="contract-settings" element={<ContractSettings />} />
            <Route path="homepage" element={<HomepageManager />} />
            <Route path="content" element={<ContentManager />} />
            <Route path="gallery" element={<GalleryManager />} />
            <Route path="bookings" element={<BookingsManager />} />
            <Route path="inquiries" element={<InquiriesManager />} />
            <Route path="inquiries/:id" element={<InquiryDetailsPage />} />
            <Route path="email" element={<EmailManager />} />
            <Route path="files" element={<FileLibrary />} />
            <Route path="pricing" element={<PricingManager />} />
            <Route path="faq" element={<FAQManager />} />
            <Route path="backup" element={<BackupManager />} />
            <Route path="*" element={<DashboardHome />} />
          </Routes>
        </div>
      </main>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
}
